'use client'

import Alert from '@components/ui/alert'
import Button from '@components/ui/button'
import ProductCardAlpine from '@components/product/product-cards/product-card-alpine'
import ProductCardLoader from '@components/ui/loaders/product-card-loader'
import cn from 'classnames'
import { Product } from '@framework/types'
import { LIMITS } from '@framework/utils/limits'
import { ROUTES } from '@utils/routes'
import { useTranslation } from 'src/app/i18n/client'

interface ProductGridProps {
  className?: string
  products: Product[]
  isLoading: boolean
  hasNextPage?: boolean
  isFetchingNextPage?: boolean
  loadMore?: () => void
  lang: string
}

export function ProductGrid({ 
  className = '', 
  products = [],
  isLoading = false,
  hasNextPage = false,
  isFetchingNextPage = false,
  loadMore,
  lang,
}: ProductGridProps) {
  const { t } = useTranslation(lang, 'common')

  if (isLoading && !products.length) {
    return (
      <div className={cn('grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6 3xl:grid-cols-7 gap-3 md:gap-4 2xl:gap-5', className)}>
        {Array.from({ length: LIMITS.PRODUCTS_LIMITS }).map((_, idx) => (
          <ProductCardLoader
            key={`product-loader-${idx}`}
            uniqueKey={`product-loader-${idx}`}
          />
        ))}
      </div>
    )
  }

  if (!products.length) {
    return (
      <div className="col-span-full">
        <Alert message={t('text-no-products')} className="max-w-lg mx-auto" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className={cn('grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6 3xl:grid-cols-7 gap-3 md:gap-4 2xl:gap-5', className)}>
        {products.map((product) => (
          <ProductCardAlpine
            key={`product--key${product.id}`}
            product={product}
            lang={lang}
            href={`${ROUTES.ANGEBOTE}/${product.id}`}
          />
        ))}
      </div>

      {hasNextPage && (
        <div className="flex justify-center mt-8">
          <Button
            onClick={() => loadMore?.()}
            disabled={isFetchingNextPage}
            className="text-sm font-semibold h-11 md:h-12 px-5 bg-brand text-brand-light rounded-md hover:bg-brand-dark transition-colors duration-200"
          >
            {isFetchingNextPage ? t('text-loading') : t('text-load-more')}
          </Button>
        </div>
      )}
    </div>
  )
}
